import React, { useState } from "react";
import Axios from "axios";
import { NotificationManager } from "react-notifications";
import { Button, Field } from "../../components";
import { EMAIL_REGEX } from "../../utils/validation";
import useLanguage from "../../hooks/language";
import useLocalStorageCountdown from "../../hooks/use-local-storage-countdown";
import ENDPOINTS from "../../utils/endpoints";
import { Footer, Header } from "../../components";
import { RATE_LIMIT_EXCEEDED } from "../../utils/api-error-types";
import { suppressErrorNotificationForTypes } from "../../utils/axios-error-notification";
import {
    AUTH_EMAIL_COOLDOWN_SECONDS,
    getRetryAfterSeconds,
    isRateLimitError,
} from "../../utils/auth-email-rate-limit";

const PASSWORD_RESET_TIMER_STORAGE_KEY = "passwordResetTimerEnd";

const PasswordForgottenPage: React.FunctionComponent = () => {
    const { translations } = useLanguage();
    const t = translations.forgottenPassword;

    // States

    const [submitted, setSubmitted] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const {
        secondsRemaining: submitCooldown,
        start: startSubmitCooldown,
    } = useLocalStorageCountdown(PASSWORD_RESET_TIMER_STORAGE_KEY, 0);

    // Errors

    const emailError = !EMAIL_REGEX.test(email) ? t.invalidEmailError : null;

    // Handlers

    const requestPasswordResetEmail = async () => {
        setSubmitted(true);

        if (emailError || submitCooldown > 0) return;

        setLoading(true);

        try {
            await Axios.put(
                ENDPOINTS.REQUEST_RESET_PASSWORD_EMAIL,
                { email },
                suppressErrorNotificationForTypes([RATE_LIMIT_EXCEEDED])
            );

            NotificationManager.success(t.successMessage);
            startSubmitCooldown(AUTH_EMAIL_COOLDOWN_SECONDS);
        } catch (error) {
            if (isRateLimitError(error)) {
                startSubmitCooldown(getRetryAfterSeconds(error));
                NotificationManager.error(t.rateLimitWaitMessage);
            }
        } finally {
            setLoading(false);
        }
    };

    // Rendering

    return (
        <div className="page">
            <Header />

            <div className="preauth-container">
                <h2>{t.header}</h2>
                <p>
                    {t.rhetoricalQuestion} {t.instruction}
                </p>
                <div className="input-holder">
                    <label>{t.emailLabel}</label>
                    <Field
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        type="email"
                        onPressEnter={requestPasswordResetEmail}
                        error={submitted ? emailError : null}
                    />
                </div>
                <Button
                    disabled={submitCooldown > 0}
                    loading={loading}
                    onClick={requestPasswordResetEmail}
                >
                    {t.sendButtonText}
                    {submitCooldown > 0 ? ` (${submitCooldown})` : ""}
                </Button>
            </div>

            <Footer />
        </div>
    );
};

export default PasswordForgottenPage;
